<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, toRef, watch } from "vue"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useConfirm } from "primevue/useconfirm"
import { useToast } from "primevue/usetoast"
import type { Services } from "@services"
import { useTaskStore } from "@core/stores/task.store"
import type {
    DimensionnementPvAnalysis,
    PvSegmentParam,
} from "@modules/pv/types/dimensionnement-pv.types"
import { usePvStore } from "@modules/pv/stores/pv.store"
import { usePvPageShell } from "@modules/pv/composables/usePvPageShell"
import { mergePvDimensionnementCharts } from "@modules/pv/utils/mergePvDimensionnementCharts"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            noPdl: "Il n’y a pas de compteur électrique (PDL) sur ce bâtiment pour lancer une étude photovoltaïque.",
            addPdl: "Ajouter un compteur électrique",
            noStudy:
                "Aucune étude de dimensionnement photovoltaïque n’a été réalisée pour le moment.",
            ctaCreate: "Créer une étude",
            actionExportData: "Exporter les données",
            loadError:
                "Impossible de charger les études de dimensionnement photovoltaïque pour ce compteur.",
            deleteTitle: "Supprimer l’analyse",
            deleteMessage:
                "Supprimer définitivement l’analyse de dimensionnement « {name} » ?",
            deleteConfirm: "Supprimer",
            deleteCancel: "Annuler",
            deleteSuccess: "Analyse supprimée",
            deleteError: "La suppression a échoué",
            reportError: "Impossible de lancer la génération du rapport",
            exportError: "L’export des données a échoué",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const services = inject<Services>("services")
if (!services) {
    throw new Error("PageBuildingPVDimensionnement: services non injectés")
}
const { $generateIotAnalyseReport, $deleteAnalysePvById } = services

const pvStore = usePvStore()
const shell = usePvPageShell()
const isSubmittingDimensionnement = toRef(shell, "isSubmittingDimensionnement")
const buildingStore = useBuildingStore()
const taskStore = useTaskStore()
const confirm = useConfirm()
const toast = useToast()
const {
    sourceId,
    isFetchingLastEtudePvBySource,
    isFetchingDimensionnementHistoryBySource,
    hasErrorLastEtudePvBySource,
    hasErrorDimensionnementHistoryBySource,
} = storeToRefs(pvStore)
const { isFetchingBuildingSources } = storeToRefs(buildingStore)
const isDeleting = ref(false)

/**
 * Clé stable bâtiment + PDL (alignée sur le wrapper PV) pour déclencher uniquement ici
 * le `GET …/dimensionnement/…/source/…/last`.
 */
const getDimensionnementLastKey = computed((): string | null => {
    const bid = shell.getBuildingId.value
    if (!bid) return null
    if (isFetchingBuildingSources.value) return null
    if (buildingStore.hasErrorBuildingSources) return null
    const sid = sourceId.value ?? pvStore.getDefaultPdlSourceId()
    if (!sid) return null
    return `${bid}:${sid}`
})

async function loadDimensionnementLast(): Promise<void> {
    const bid = shell.getBuildingId.value
    if (!bid) return
    if (isFetchingBuildingSources.value) return
    if (buildingStore.hasErrorBuildingSources) return

    const sid = sourceId.value ?? pvStore.getDefaultPdlSourceId()
    if (!sid) return

    if (pvStore.getSourceId !== sid) {
        pvStore.setSourceId(sid, { fetchSourcePv: false })
    }
    const resolvedSourceId = sourceId.value
    if (!resolvedSourceId) return

    const ids = { sourceId: resolvedSourceId, buildingId: bid }
    await pvStore.fetchLastEtudePvBySource(ids)
}

async function refreshDimensionnementPage(): Promise<void> {
    const bid = shell.getBuildingId.value
    const sid = sourceId.value
    if (!bid || !sid) return
    const ids = { sourceId: sid, buildingId: bid }
    await Promise.all([
        pvStore.fetchLastEtudePvBySource(ids),
        pvStore.fetchDimensionnementHistoryBySource(ids),
        pvStore.fetchLastEtudeEcoForSource(ids),
        pvStore.fetchValorisationHistoryBySource(ids),
    ])
}

watch(
    getDimensionnementLastKey,
    (key) => {
        if (!key) return
        void loadDimensionnementLast()
    },
    { immediate: true }
)

// ================================
// DONNÉES CALCULÉES
// ================================
const getLastAnalyse = computed(
    () => pvStore.getLastEtudePv?.lastAnalyse ?? null
)

const getTemplateCharts = computed(() => {
    const tpl = pvStore.getLastEtudePv?.template
    if (!tpl || typeof tpl !== "object") return null
    const charts = (tpl as { charts?: unknown }).charts
    return charts ?? null
})

const getMergedDimensionnementCharts = computed(() =>
    mergePvDimensionnementCharts(
        getLastAnalyse.value?.charts,
        getTemplateCharts.value
    )
)

/** Coordonnées bâtiment `[lat, lng]` pour la carte des pans (aligné legacy `getAdressLocation`). */
const getBuildingLatLng = computed((): [number, number] | null => {
    const coords = buildingStore.building?.location?.coordinates
    if (
        Array.isArray(coords) &&
        coords.length >= 2 &&
        typeof coords[0] === "number" &&
        typeof coords[1] === "number"
    ) {
        return [coords[1], coords[0]]
    }
    return null
})

/** `true` pendant le chargement des compteurs et de la dernière étude PV (même logique que la page étude économique). */
const getIsPvDimensionnementPageLoading = computed(() => {
    const bid = shell.getBuildingId.value
    if (!bid) return false
    if (isFetchingBuildingSources.value) return true
    const sid = sourceId.value ?? pvStore.getDefaultPdlSourceId()
    if (!sid) return false
    return (
        isFetchingLastEtudePvBySource.value ||
        isFetchingDimensionnementHistoryBySource.value
    )
})
const getHasPageError = computed(
    () =>
        hasErrorLastEtudePvBySource.value ||
        hasErrorDimensionnementHistoryBySource.value
)

async function onClickDownloadReport(
    analyse: DimensionnementPvAnalysis
): Promise<void> {
    const analyseWithReport = analyse as DimensionnementPvAnalysis & {
        url_rapport?: string
        date_expiration_rapport?: string
    }
    if (
        analyseWithReport.url_rapport &&
        analyseWithReport.date_expiration_rapport &&
        new Date(analyseWithReport.date_expiration_rapport) > new Date()
    ) {
        window.location.assign(analyseWithReport.url_rapport)
        return
    }

    try {
        const { task_id, event_name } = await $generateIotAnalyseReport(
            analyse._id
        )
        taskStore.followEvent(task_id, event_name, {
            taskname: "analyse.pv.dimensionnement.rapport",
            taskToastTitle: t("actionExportData"),
            notification: true,
            autoDelete: true,
            onSuccess: () => {
                void refreshDimensionnementPage()
            },
        })
    } catch {
        toast.add({
            severity: "error",
            summary: t("reportError"),
            life: 6000,
        })
    }
}

async function onClickDownloadData(
    analyse: DimensionnementPvAnalysis
): Promise<void> {
    try {
        await taskStore.launchTask(
            "analyses.export_charts_excel.request",
            { _id_analyse: analyse._id },
            undefined,
            {
                taskname: "analyses.export_charts_excel.request",
                taskToastTitle: t("actionExportData"),
                notification: true,
                autoDelete: true,
            }
        )
    } catch {
        toast.add({
            severity: "error",
            summary: t("exportError"),
            life: 6000,
        })
    }
}

function onClickDeleteAnalyse(analyse: DimensionnementPvAnalysis): void {
    const name =
        typeof analyse.name_analyse === "string" &&
        analyse.name_analyse.length > 0
            ? analyse.name_analyse
            : analyse._id
    confirm.require({
        header: t("deleteTitle"),
        message: t("deleteMessage", { name }),
        icon: "pi pi-exclamation-triangle",
        rejectLabel: t("deleteCancel"),
        acceptProps: {
            icon: "pi pi-trash",
            label: t("deleteConfirm"),
            severity: "danger",
        },
        rejectProps: {
            label: t("deleteCancel"),
            severity: "secondary",
            outlined: true,
        },
        accept: async () => {
            isDeleting.value = true
            const snap = pvStore.snapshotPvListsAndLastStudies()
            pvStore.removeAnalyseFromHistory(analyse._id)
            try {
                await $deleteAnalysePvById(analyse._id)
                toast.add({
                    severity: "success",
                    summary: t("deleteSuccess"),
                    life: 4000,
                })
            } catch {
                pvStore.restorePvListsAndLastStudies(snap)
                toast.add({
                    severity: "error",
                    summary: t("deleteError"),
                    life: 6000,
                })
            } finally {
                isDeleting.value = false
            }
        },
        reject: () => {},
    })
}

function onRelaunchSegments(segments: PvSegmentParam[]): void {
    const analyse = getLastAnalyse.value
    if (!analyse?._id) return
    void shell.onRelaunchDimensionnementWithSegments(analyse, segments)
}

function onClickLaunchEtudeEcoFromScenario(
    analyse: DimensionnementPvAnalysis
): void {
    shell.onClickModalEtudeEco(analyse._id)
}
</script>

<template>
    <div class="page page-building-pv-dimensionnement p-0">
        <template v-if="getIsPvDimensionnementPageLoading">
            <BaseSkeleton height="39rem" class="w-full rounded-md" />
        </template>
        <template v-else>
            <BaseMessage
                v-if="getHasPageError"
                severity="error"
                :closable="false"
            >
                {{ t("loadError") }}
            </BaseMessage>
            <BaseMessage
                v-else-if="!pvStore.getSourceId"
                severity="info"
                :closable="false"
                class="w-full"
            >
                <div
                    class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                >
                    <span>{{ t("noPdl") }}</span>
                    <BaseButton
                        :label="t('addPdl')"
                        icon="pi pi-plus"
                        outlined
                        class="shrink-0"
                        @click="shell.onClickAddPdl"
                    />
                </div>
            </BaseMessage>
            <BaseMessage
                v-else-if="!getLastAnalyse?._id"
                severity="info"
                :closable="false"
                class="w-full"
            >
                <div
                    class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                >
                    <span>{{ t("noStudy") }}</span>
                    <BaseButton
                        icon="pi pi-plus"
                        outlined
                        :label="t('ctaCreate')"
                        class="shrink-0"
                        @click="shell.onClickCreateDimensionnement"
                    />
                </div>
            </BaseMessage>
            <template v-else>
                <div class="flex flex-col gap-10">
                    <PVDimensionnementSegmentsPanel
                        :scenario-name="
                            getLastAnalyse.name_analyse?.trim() || 'Étude PV'
                        "
                        :segments="getLastAnalyse.inputs?.list_params_pv"
                        :address-location="getBuildingLatLng"
                        :disabled="isSubmittingDimensionnement"
                        @click:relaunch="onRelaunchSegments"
                    />
                    <PVDimensionnementScenarioPanel
                        :analyse="getLastAnalyse"
                        :merged-charts="getMergedDimensionnementCharts"
                        :busy="isSubmittingDimensionnement"
                        :is-deleting="isDeleting"
                        @click:modify="shell.onClickEditDimensionnement"
                        @click:download-data="onClickDownloadData"
                        @click:download-report="onClickDownloadReport"
                        @click:delete="onClickDeleteAnalyse"
                        @click:launch-etude-eco="
                            onClickLaunchEtudeEcoFromScenario
                        "
                    />
                </div>
            </template>
        </template>
    </div>
</template>
